export const ReleaseData = [
{
    version: "v0.2.4",
    date: "October 18, 2026",
    title: "New Emulators, Enhancements & Bug Fixes",
    changes: [
      {
        title: "The following applications (emulators) have been added:",
        items: [
          "*Atari Jaguar* (experimental)",
          "*Atari Jaguar CD* (experimental)",
          "*Sega 32X*",
          "*Sega 32X CD*",
          "*Sega Dreamcast* (experimental)",
          "*Sony PSP* (experimental)"
        ]
      },
      {
        title: "New application variants have been added for the following aliases (systems):",
        items: [
          "*Atari 7800*: Libretro ProSystem-Next (supports shaders, cheats, and achievements)",
          "*Nintendo 64*: Libretro Mupen64Plus-Next"
        ]
      },
      {
        title: "The following applications are no longer experimental:",
        items: [
          "*Apple IIGS*",
          "*Atari 5200*",
          "*Nintendo 64*"
        ]
      },
      {
        title: "Player",
        items: [
          "Added a standardized on-screen *Pause* button, a consistent *On-Screen Controls* setting, and a matching keyboard/gamepad control scheme across applications."
        ]
      },
      {
        title: "General",
        items: [
          "Fixed an issue where streaming downloads would fail if the server's response didn't include a filename in its headers.",
          "Removed the *Experimental apps* setting. Experimental applications are no longer hidden behind a toggle - they always appear, and are simply marked with an *experimental* tag in the editor's Platform Chooser instead.",
          "Cloud storage (Dropbox) downloads now automatically retry on transient failures, matching the retry behavior uploads already had."
        ]
      },
      {
        title: "Apple II / Apple IIGS",
        items: [
          "You can now map the *D-pad* to a keyboard key or gamepad button from the *Feed Editor*.",
          "Added optional support for a second 5.25\" (Apple II/IIGS) or 3.5\" (Apple IIGS) disk drive, for games that use one. Enable it per item in the *Feed Editor*.",
          "Improved the disk-swapping menu in the pause screen: switching one disk drive no longer visually disrupts other selectors, and selecting the same disk for two drives is now clearly flagged instead of silently resolved.",
          "Added an *Initial Keyboard Mode* option (Simulate Joystick / Full Keyboard) in the *Feed Editor*, for keyboard-only applications and games that don't use a joystick. Can still be toggled in-game afterward either way.",
          "*Open Apple* and *Closed Apple* on the virtual keyboard now work like *Shift*/*Control*: tap to hold, tap again to release.",
          "Fixed an issue where mapping the *A* or *B* button to a keyboard key had no effect.",
          "Fixed *Apple IIGS* 2nd drive switches always appearing off when reopening an item in the editor.",
          "Adding a disk image via URL or local upload in the *Feed Editor* now automatically assigns 3.5\" disks (800K or larger) to *Apple IIGS* instead of *Apple II*.",
          "Fixed *.nib* disk images always being assigned to *Commodore 64* in the *Feed Editor*; Apple II *.nib* images are now detected correctly.",
          "Added a *Save Disks* option (Apple II) in the *Feed Editor*. This lets a player insert blank, pre-formatted disks for games that need one to save progress (e.g. a \"character disk\"), without having to manually source and upload a blank disk image.",
          "Fixed the pause screen's disk selector freezing when *Disk Drive 1* was set to \"(none)\".",
          "Fixed save states silently failing to save or load correctly when a 2nd disk drive was in use.",
          "Fixed loading a save state leaving whatever disk was currently in a drive in place instead of ejecting it, when that drive was empty at the time the state was saved. (*Apple IIGS* fixes apply across all four drive slots.)"
        ]
      },
      {
        title: "Atari 5200",
        items: [
          "Added an on-screen *Pause*/*Keypad* button and a stick-driven radial keypad selector, matching *Atari Jaguar* and *ColecoVision*."
        ]
      },
      {
        title: "Atari 7800",
        items: [
          "*Libretro ProSystem-Next* is now the default *Atari 7800* core (previously *JS7800*, which remains available as a lighter-weight alternative)."
        ]
      },
      {
        title: "Atari Jaguar / Jaguar CD",
        items: [
          "Includes a stick-driven radial menu for quickly selecting from the Jaguar's unique 12-key numeric keypad using the right analog stick. For games that originally shipped with a dedicated keypad overlay, the real button labels from that overlay are shown instead of generic numbers."
        ]
      },
      {
        title: "Bally Astrocade",
        items: [
          "Fixed the on-screen keypad reopening if *Enter* was still held down when it closed."
        ]
      },
      {
        title: "ColecoVision",
        items: [
          "Added an on-screen *Pause*/*Keypad* button and a stick-driven radial keypad selector, matching *Atari Jaguar*."
        ]
      },
      {
        title: "Commodore 64",
        items: [
          "Fixed the *Save Disks* setting ignoring an explicit choice of \"(none)\" and silently defaulting to one save disk anyway."
        ]
      },
      {
        title: "Nintendo 64",
        items: [
          "*Libretro Mupen64Plus-Next* is now the default *Nintendo 64* core (previously *ParaLLEl N64*, which remains available as an alternative).",
          "Fixed *paraLLEl N64* accidentally opening *Pause* when using *Ctrl+Shift* as analog movement modifiers during normal play."
        ]
      },
      {
        title: "Nintendo DS",
        items: [
          "Added a selectable microphone \"blow\" type (*Realistic Sample* or *Random Noise*), in the Feed Editor and the in-game pause menu. Different games respond better to different types: a recorded sample fixes blow detection in some games (e.g. *Zelda: Phantom Hourglass*), but reduces reliability in others, so it's now a choice instead of one setting for everyone."
        ]
      },
      {
        title: "SNES",
        items: [
          "Fixed the pause menu occasionally not responding to *Tab*, *Enter*, or *Space*."
        ]
      },
      {
        title: "Achievements",
        items: [
          "Fixed a missing RetroAchievements condition type that caused some achievements to fail to unlock (e.g. several in *The Legend of Zelda: Phantom Hourglass*)."
        ]
      },
      {
        title: "Editor",
        items: [
          "Editor dialogs now show an indicator when a tab has more content below than fits on screen, so it's clearer when scrolling is needed.",
          "Added an *Arrange* dialog to the Categories toolbar that lets you quickly drag and drop categories to change their order.",
          "Fixed dragging to reorder media in the *Merge* dialog not scrolling the list reliably when dragging near the top or bottom edge.",
          "URL fields (Discs, Media, BIOS, etc.) now show parsed file names, a full-path toggle, and drag-and-drop reordering for multi-URL fields. *Raw* text mode is still available.",
          "Added a *Use classic URL entry fields* setting (Settings > Editor) to revert to the previous plain-text fields.",
          "Deleting categories or items now asks for confirmation, since deleting a category also removes everything inside it.",
          "Fixed the Cheats tab's *Cheat File* field not supporting local file uploads.",
          "Fixed local file and URL adds for disc-based systems (CD-i, 32X CD, Jaguar CD, Saturn, etc.) showing the raw filename as the title instead of the matched game's real name, even when artwork was found correctly.",
          "Fixed matched game titles that include a version number (e.g. \"Sonic R v1.000\") failing to match, and not displaying cleanly when they did.",
          "Adding a URL for a large disc-image format (.cdi, .chd, .pbp, .cso, .iso) that can't be matched via hash now fails immediately instead of downloading the entire file first.",
          "Fixed per-game control mappings and settings (Atari 5200, ColecoVision, Bally Astrocade, Atari Jaguar, 3DO) not being applied when a title is picked from the Add Item search dropdown. They previously only applied when a file was matched by hash.",
          "Selecting an item's platform/core now opens the same Platform Chooser used when adding a new item, instead of a plain dropdown.",
          "Application/platform icons now appear next to the relevant dropdowns in the Feed Editor (Applications settings, Feed properties, and item type selection), making it easier to confirm the right one is selected."
        ]
      },
      {
        title: "Cheats",
        items: [
          "Fixed some cheats not reverting their effect when disabled, so the last-applied value could stay stuck in memory. This affected a subset of games, not all cheats."
        ]
      },
      {
        title: "Improvements",
        items: [
          "The on-screen keyboard/gamepad toggle button now appears even when the first keys pressed are mapped to the joystick (Apple II, Apple IIGS, Commodore 8-bit)."
        ]
      }
    ],
    image: "images/update5.png"
},
{
    version: "v0.2.3",
    date: "July 18, 2026",
    title: "New Emulators & Editor Enhancements",
    changes: [
      {
        title: "The following applications (emulators) have been added:",
        items: [
          "*Bally Astrocade*",
          "*Philips CD-i*",
          "*Apple II*",
          "*Apple IIGS*"
        ]
      },
      {
        title: "Nintendo DS",
        items: [
          "Added *Homebrew SD Card* support. When enabled, the emulator provides an SD card via DLDI patching, allowing homebrew games to read and write files. An optional *SD Card Archive* (zip file) and *SD Card Path* can be specified to pre-populate the card with files at startup."
        ]
      },
      {
        title: "Editor: Application Selection",
        items: [
          "Added a new *Browse...* dialog for selecting applications when editing items. The dialog features a visual grid layout with platform thumbnails, categorized by manufacturer (Atari, Nintendo, Sega, NEC, Sony, SNK, Arcade, Computers, Other)."
        ]
      },
      {
        title: "Cloud Storage (Dropbox)",
        items: [
          "Fixed failures that could occur when downloading or uploading files."
        ]
      }
    ],
    image: "images/update4.png"
  },
{
    version: "v0.2.2",
    date: "June 3, 2026",
    title: "RetroAchievements & Local File Upload",
    changes: [
      {
        title: "RetroAchievements",
        items: [
          "Added *RetroAchievements* support. Log in via *Settings* > *RetroAchievements* to earn and track achievements while you play.",
          "Achievements are supported for the following systems: *Atari 2600*, *Atari Lynx*, *Bandai WonderSwan / Color*, *ColecoVision*, *NEC PC Engine / TurboGrafx-16*, *NEC PC-FX*, *NEC SuperGrafx*, *Nintendo NES*, *Nintendo SNES*, *Nintendo 64*, *Nintendo Game Boy*, *Nintendo Game Boy Color*, *Nintendo Game Boy Advance*, *Nintendo DS*, *Nintendo Pokémon Mini*, *Nintendo Virtual Boy*, *Panasonic 3DO*, *Sega SG-1000*, *Sega Master System*, *Sega Genesis*, *Sega CD*, *Sega Game Gear*, *Sega Saturn*, *SNK Neo Geo CD*, *SNK Neo Geo Pocket / Color*, *Sony PlayStation*.",
          "When a game has achievements, an *Achievements* button will appear in the pause menu where you can see your progress.",
          "A notification will pop up on screen whenever an achievement is unlocked.",
          "Supports *softcore* mode, *rich presence*, and an in-game achievement list showing earned and unearned achievements for the current game.",
          "Note: *Hardcore mode* is not currently supported."
        ]
      },
      {
        title: "Player",
        items: [
          "Reworked pause menu layout and navigation to accommodate *Cheats* and *Achievements* buttons."
        ]
      },
      {
        title: "Editor: Cloud Storage",
        items: [
          "The editor now supports uploading both the feed and game assets (ROMs, disc images, BIOS files, images, etc.) directly to cloud storage. *Note: Cloud storage must be enabled in Settings to use these features.*",
          "Feeds can now be exported directly to cloud storage.",
          "Local files and folders can be uploaded via drag-and-drop onto the editor, or via the new *Add Local Files* and *Add Local Folder* options. Dropping a folder will recursively upload all supported files within it.",
          "The *Add Local Files* dialog shows upload progress for each file, with separate tabs for completed, skipped, and errored files.",
          "Image fields (thumbnails, backgrounds) now support uploading local files via drag-and-drop or via the field menu.",
          "File fields (ROMs, BIOS files, etc.) now support uploading local files via drag-and-drop or via the *Add file(s)...* option in the field menu.",
          "When adding local files, the editor will automatically determine the application type for files that weren't assigned one during upload, where possible."
        ]
      },
      {
        title: "Editor: General",
        items: [
          "Added a *Merge* dialog for combining like items of the same type into a single item, consolidating their discs and media into one entry."
        ]
      },
      {
        title: "Atari 2600",
        items: [
          "Improved audio support for iOS."
        ]
      },
      {
        title: "DOS",
        items: [
          "Added a *CPU Speed* property to DOS game settings in the editor. This allows selecting a CPU speed preset ranging from an 8086 at 4.77 MHz (1980) up to an AMD Athlon at 1.2 GHz (2000), or Maximum Performance. The default uses DOSBox's automatic CPU speed detection."
        ]
      },
      {
        title: "Nintendo DS",
        items: [
          "Added a *Firmware Language* property to Nintendo DS game settings in the editor. This allows specifying the language reported by the DS firmware ((Auto), English, Japanese, French, German, Italian, Spanish). Note: the selected language is only used if the game supports it."
        ]
      }
    ],
    image: "images/update3.png"
  },
{
    version: "v0.2.1",
    date: "April 29, 2026",
    title: "Cheats, Search & Bug Fixes",
    changes: [
      {
        title: "Cheats",
        items: [
          "Added *Cheats* support to the following systems: *Atari 2600*, *Atari 5200*, *Atari Lynx*, *NEC PC Engine / TurboGrafx-16*, *NEC PC Engine CD / TurboGrafx-CD*, *NEC SuperGrafx*, *Nintendo NES*, *Nintendo SNES*, *Nintendo Game Boy*, *Nintendo Game Boy Color*, *Nintendo Game Boy Advance*, *Nintendo DS*, *Sega SG-1000*, *Sega Master System*, *Sega Genesis*, *Sega CD*, *Sega Saturn*, *Sega Game Gear*, *Sony PlayStation*, *Quake*."
        ]
      },
      {
        title: "Player",
        items: [
          "Added *Search* support to the player, allowing you to quickly find games across all categories.",
          "Fixed an issue on *iOS* where closing a search or feed dialog after typing would leave the page scrolled to an incorrect position."
        ]
      },
      {
        title: "Editor",
        items: [
          "Added *Search* tab to the Feed Editor, allowing you to search, edit, copy, and analyze items across all categories.",
          "Added a *Cheats* tab to item properties for supported systems. A cheat file can be specified via URL, or selected from the built-in cheat database."
        ]
      },
      {
        title: "Bugs",
        items: [
          "Fixed an issue where gamepad navigation was not functioning in the *Shader Settings* tab of the *Nintendo DS* emulator settings.",
          "Fixed an issue where gamepad navigation was not functioning in the *Display* and *Shader Settings* tabs of the *Pokémon Mini* emulator settings."
        ]
      }
    ],
    image: "images/update2.png"
  },
{
    version: "v0.2.0",
    date: "March 23, 2026",
    title: "New Apps, Defaults & Shaders",
    changes: [
      {
        title: "The following applications have been added:",
        items: [
          "*Nintendo Pokémon Mini*",
          "*Sega Saturn* (Note: Requires *Experimental apps* to be enabled in *Settings* > *Advanced*)."
        ]
      },
      {
        title: "New application variants have been added and set as the *Default* for the following aliases (systems):",
        items: [
          "Atari Lynx: *Libretro Mednafen Lynx*",
          "Bandai WonderSwan / Color: *Libretro Mednafen Wswan*",
          "NEC PC Engine: *Libretro Mednafen PCEngine*",
          "NEC SuperGrafx: *Libretro Mednafen SuperGrafx*",
          "Nintendo Game Boy / Color: *Libretro SameBoy*",
          "Nintendo GBA: *Libretro mGBA*",
          "Nintendo NES: *Libretro FCEUmm*",
          "Nintendo SNES: *Libretro SNES9X*",
          "Nintendo Virtual Boy: *Libretro Mednafen VB*",
          "Sega Game Gear / Genesis / Master System / SG-1000: *Libretro Genesis Plus GX*",
          "SNK Neo Geo Pocket / Color: *Libretro Mednafen NGP*"
        ]
      },
      "You can now select the default *Application* to use for each alias (SNES, etc.) in *Settings* > *Applications*.",
      "*Important:* Because application defaults have changed, previous saves will not appear unless you map the previous application to the alias in *Settings* > *Applications*, or configure the specific item (game, etc.) to use the previous default application in the *Feed Editor*.",
      "Feeds can now specify the default application for an alias (SNES, etc.) via the *Feed Editor* (*Edit Feed* > *Applications* tab). *Note:* Device-specific user settings override feed settings.",
      "Added *Shader* support for many applications. These can be accessed via *Settings* > *Shaders* in the pause menu.",
      "Standalone link generation in the *Feed Editor* has changed from using *TinyURL* to *is.gd*."
    ],
    image: "images/update.png"
  }
];

// TODO: Add bilinear info
// TODO: Add sound updates across the board
